import {
  AlertTriangle,
  CircleCheck,
  Flame,
  HeartPulse,
  HelpCircle,
  Stethoscope,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ascleo/ui/lib/utils'
import type { PrescriptionRequestReason } from '../machine'
import { PrescriptionSectionHeading } from './prescription-section-heading'

const RX_REASONS: ReadonlyArray<{ id: PrescriptionRequestReason; icon: LucideIcon }> = [
  { id: 'schmerzen', icon: Zap },
  { id: 'entzuendung', icon: Flame },
  { id: 'nachsorge', icon: Stethoscope },
  { id: 'allergie', icon: AlertTriangle },
  { id: 'chronisch', icon: HeartPulse },
  { id: 'anderes', icon: HelpCircle },
]

/**
 * Renders the prescription request reason grid as a radio group whose
 * selected card shows a check mark and a strong border (KO-86, FC-08).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PrescriptionReasonSection requestReason={undefined} onSelectReason={() => undefined} />
 * ```
 */
export function PrescriptionReasonSection({
  requestReason,
  onSelectReason,
}: {
  requestReason: PrescriptionRequestReason | undefined
  onSelectReason: (reason: PrescriptionRequestReason) => void
}) {
  const { t } = useTranslation()

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <PrescriptionSectionHeading
        id="prescription-reason-heading"
        number={1}
        title={t('self-service.prescription.medication-selection.reason-title')}
        hint={t('self-service.prescription.medication-selection.reason-hint')}
      />
      <div
        role="radiogroup"
        aria-labelledby="prescription-reason-heading"
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        {RX_REASONS.map(({ id, icon: Icon }) => {
          const active = requestReason === id
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`frontdesk-prescription-reason-${id}`}
              onClick={() => onSelectReason(id)}
              className={cn(
                'flex min-h-16 items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left text-lg font-medium transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45',
                active
                  ? 'border-primary bg-primary-subtle text-primary-ink'
                  : 'border-border bg-card text-foreground hover:bg-foreground-soft',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'grid size-10 shrink-0 place-items-center rounded-xl',
                  active ? 'bg-primary/15' : 'bg-foreground-soft text-foreground',
                )}
              >
                <Icon className="size-6" />
              </span>
              <span className="min-w-0 flex-1">
                {t(`self-service.prescription.medication-selection.reason-${id}`)}
              </span>
              {active ? <CircleCheck aria-hidden className="size-6 shrink-0" /> : null}
            </button>
          )
        })}
      </div>
    </section>
  )
}
